/**
 * Presentation for HRM in-app notification types.
 *
 * `notif_type` is a free varchar in public.notifications on purpose — a new producer can raise
 * a new type without a back-end release. These maps only decorate the known ones; anything
 * unrecognised falls back to DEFAULT_NOTIF_* so an unmapped type still renders correctly.
 */

/**
 * The `<DOC>_REQUEST` / `<DOC>_DECISION` pairs are raised by hrm-service's ApprovalNotifier, one
 * pair per ApprovalDocTypeEnum constant: _REQUEST lands in an approver's queue, _DECISION lands
 * with the applicant. Adding a doc type to that enum means adding its pair here.
 */
export const NOTIF_TYPE_LABEL: {[key: string]: string} = {
  LEAVE_REQUEST: 'Leave Request',
  LEAVE_DECISION: 'Leave Decision',
  ATTENDANCE_REQUEST: 'Attendance Request',
  ATTENDANCE_DECISION: 'Attendance Decision',
  LOAN_REQUEST: 'Loan Request',
  LOAN_DECISION: 'Loan Decision',
  COMP_OFF_REQUEST: 'Compensatory Claim Request',
  COMP_OFF_DECISION: 'Compensatory Claim Decision',
  APPRAISAL_REQUEST: 'Appraisal Request',
  APPRAISAL_DECISION: 'Appraisal Decision',
  INCREMENT_REQUEST: 'Increment Request',
  INCREMENT_DECISION: 'Increment Decision',
  PROMOTION_REQUEST: 'Promotion Request',
  PROMOTION_DECISION: 'Promotion Decision',
  SEPARATION_REQUEST: 'Separation Request',
  SEPARATION_DECISION: 'Separation Decision',
  APPRAISAL_DUE: 'Appraisal',
  PAYSLIP_PUBLISHED: 'Payslip',
  ANNOUNCEMENT: 'Announcement',
}

/**
 * Tone is per notification *type*, not per outcome — a `_DECISION` chip reads the same whether the
 * document was approved or rejected (the title carries the outcome). `_REQUEST` is amber because it
 * means "someone is waiting on you".
 */
export const NOTIF_TYPE_TONE: {[key: string]: string} = {
  LEAVE_REQUEST: 'warning',
  LEAVE_DECISION: 'success',
  ATTENDANCE_REQUEST: 'warning',
  ATTENDANCE_DECISION: 'success',
  LOAN_REQUEST: 'warning',
  LOAN_DECISION: 'success',
  COMP_OFF_REQUEST: 'warning',
  COMP_OFF_DECISION: 'success',
  APPRAISAL_REQUEST: 'warning',
  APPRAISAL_DECISION: 'success',
  INCREMENT_REQUEST: 'warning',
  INCREMENT_DECISION: 'success',
  PROMOTION_REQUEST: 'warning',
  PROMOTION_DECISION: 'success',
  SEPARATION_REQUEST: 'warning',
  SEPARATION_DECISION: 'success',
  APPRAISAL_DUE: 'info',
  PAYSLIP_PUBLISHED: 'primary',
  ANNOUNCEMENT: 'info',
}

export const NOTIF_TYPE_ICON: {[key: string]: string} = {
  LEAVE_REQUEST: 'event_available',
  LEAVE_DECISION: 'event_available',
  ATTENDANCE_REQUEST: 'schedule',
  ATTENDANCE_DECISION: 'schedule',
  LOAN_REQUEST: 'payments',
  LOAN_DECISION: 'payments',
  COMP_OFF_REQUEST: 'more_time',
  COMP_OFF_DECISION: 'more_time',
  APPRAISAL_REQUEST: 'star_rate',
  APPRAISAL_DECISION: 'star_rate',
  INCREMENT_REQUEST: 'trending_up',
  INCREMENT_DECISION: 'trending_up',
  PROMOTION_REQUEST: 'military_tech',
  PROMOTION_DECISION: 'military_tech',
  SEPARATION_REQUEST: 'logout',
  SEPARATION_DECISION: 'logout',
  APPRAISAL_DUE: 'star_rate',
  PAYSLIP_PUBLISHED: 'receipt_long',
  ANNOUNCEMENT: 'campaign',
}

export const DEFAULT_NOTIF_TONE = 'secondary'
export const DEFAULT_NOTIF_ICON = 'notifications'

export const NOTIF_TYPE_OPTIONS = Object.keys(NOTIF_TYPE_LABEL).map((id) => ({
  id,
  title: NOTIF_TYPE_LABEL[id],
}))

export const notifTone = (notifType?: string) => NOTIF_TYPE_TONE[notifType || ''] || DEFAULT_NOTIF_TONE
export const notifIcon = (notifType?: string) => NOTIF_TYPE_ICON[notifType || ''] || DEFAULT_NOTIF_ICON
export const notifLabel = (notifType?: string) => NOTIF_TYPE_LABEL[notifType || ''] || notifType || '—'
